import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useTheme } from "@/context/ThemeContext";
import { AdoptionSummary } from "../../api/getAdoptionSummary";

interface AdoptionPieCardProps {
  summary: AdoptionSummary | null;
  loading: boolean;
}

const AdoptionPieCard: React.FC<AdoptionPieCardProps> = ({
  summary,
  loading,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const connectedCount = summary?.connectedCount ?? 0;
  const notConnectedCount = summary?.notConnectedCount ?? 0;
  const totalCompanies = summary?.totalCompanies ?? 0;

  const colors = isDark
    ? { connected: "#94a3b8", notConnected: "#334155", surface: "#1e293b" }
    : { connected: "#475569", notConnected: "#cbd5e1", surface: "#ffffff" };

  const chartData = [
    { name: "Connected", value: connectedCount, fill: colors.connected },
    {
      name: "Not Connected",
      value: notConnectedCount,
      fill: colors.notConnected,
    },
  ];

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
      <h2 className="text-base font-normal text-gray-900 dark:text-white">
        Adoption
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        Connected vs not connected companies
      </p>

      {loading && !summary ? (
        <div className="h-56 flex items-center justify-center">
          <LoadingIndicator isFullScreen={false} />
        </div>
      ) : totalCompanies === 0 ? (
        <div className="h-56 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          No companies with an Intacct ID yet
        </div>
      ) : (
        <>
          <ResponsiveContainer height={224} width="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                innerRadius={56}
                nameKey="name"
                outerRadius={88}
                paddingAngle={2}
                stroke={colors.surface}
                strokeWidth={2}
              >
                {chartData.map((entry) => (
                  <Cell fill={entry.fill} key={entry.name} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? "#1f2937" : "#ffffff",
                  border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                  borderRadius: "0.375rem",
                  color: isDark ? "#f3f4f6" : "#1f2937",
                  fontSize: "0.875rem",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="flex items-center justify-center gap-6 mt-2">
            <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: colors.connected }}
              />
              Connected ({connectedCount.toLocaleString()})
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: colors.notConnected }}
              />
              Not Connected ({notConnectedCount.toLocaleString()})
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
            {totalCompanies.toLocaleString()} companies with an Intacct ID
          </p>
        </>
      )}
    </div>
  );
};

export default AdoptionPieCard;
